import { StateService, Transition, TransitionService, StateParams } from '@uirouter/angular';
import { EVENT_MENU_CHANGED, EVENT_MENU_READY, GA1Service, IGA1Message, MESSAGE_CHANGE_MENU } from './ga1.service';
import { Injectable, OnDestroy } from '@angular/core';
import { IGAEvent } from '../services/ga-event.service';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import { UrlService } from '../browser/url.service';
import { IObject } from '@type_definitions/datatypes';
import { urlUtil } from '@app/shared/util';

export interface IGA1MenuMessage extends IGA1Message {
  data:{
    demence:string;
    params?:string;
    returnUrl?:string;
  };
}

/**
 * This service takes care of syncing the displayed menu with a menu URL param.
 * It works with sending and receiving messages from GA1's MenuTree.js
 */
@Injectable()
export class GA1MenuService implements OnDestroy {
  private currentMenuParam:string;
  private menuReady:boolean;
  private ga1EventsSubscription:Subscription;
  private onBeforeStateHook:Function;

  constructor(
    private stateService:StateService,
    private transitionService:TransitionService,
    private ga1Service:GA1Service,
    private urlService:UrlService
  ) {
    /**/
  }

  public start():void {
    // register listener
    this.listen();

    // get the initial menuParam from the location
    this.currentMenuParam = this.urlService.urlParams?.menu;
    this.loadMenu();
  }

  private listen():void {
    this.ga1EventsSubscription = this.ga1Service.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_MENU_CHANGED) {
        this.onMenuChanged(event.data);
      }
      else if (event.name === EVENT_MENU_READY) {
        this.onMenuReady();
      }
    });

    this.onBeforeStateHook = this.transitionService.onBefore({}, this.onBeforeTransition);
  }

  public messageToMenuParam(message:IGA1MenuMessage):string {
    let menuParam:string;

    if (message.data.demence && !message.data.params) { // this is a goBack message, we look up the corresponding message from the currentMenuParam
      const splitted:string[] = this.currentMenuParam? this.currentMenuParam.split('~') : [];
      for (let i = 0; i < splitted.length; i++) {
        if ((message.data.demence === 'WPN_EDIT' && splitted[ i ].search(/WEBSHOP/) >= 0)
          || (message.data.demence === 'WPL_EDIT' && splitted[ i ].search(/LOCALSHOP/) >= 0)) {
          return splitted[ i ];
        }
      }
      return undefined;
    }

    if (message.data.demence === 'WPN_EDIT') {
      //we need to store webshopId
      const webshopId:string = message.data.params.match(/Desprid=(\d+)/)[ 1 ];
      menuParam = this.getMenuParamValue(webshopId);
    }
    if (message.data.demence === 'WPL_EDIT') {
      //we need to store all required params
      const localShopId:string = message.data.params.match(/Depcyid=(\d+)/)[ 1 ];
      const webshopId:string = message.data.params.match(/Desprid=(\d+)/)[ 1 ];
      const countryId:string = message.data.params.match(/Decouid=(\d+)/)[ 1 ];
      const bpId:string = message.data.params.match(/Deccoid=(\d+)/)[ 1 ];
      const requestId:string = message.data.params.match(/Dereqid=(\d+)/)?.[ 1 ];
      menuParam = `${this.getMenuParamValue(webshopId)}~LOCALSHOP_${localShopId}_${webshopId}_${countryId}_${bpId}`;

      if (requestId) {
        menuParam += `_${requestId}`;
      }
    }

    return menuParam;
  }

  public menuParamToMessages(menuParam:string):IGA1MenuMessage[] {
    const messages:IGA1MenuMessage[] = [];

    if (menuParam) {
      const splitted = menuParam? menuParam.split('~') : [];
      for (let i = 0; i < splitted.length; i++) {
        const p:string = splitted[ i ];

        if (p.match(/WEBSHOP_\d+/)) {
          const webshopId:string = p.match(/WEBSHOP_(\d+)+/)[ 1 ];

          const params:IObject = {
            keyfield: 'demence',
            keyvalue: 'WPN_EDIT',
            level: '2',
            previous: '1',
            withPics: '1',
            Desprid: webshopId,
            ejbEvent: 'Edit'
          };

          messages.push({
            message: MESSAGE_CHANGE_MENU,
            data: {
              demence: 'WPN_EDIT',
              params: `&${urlUtil.createUrlQueryParams(params)}`,
              returnUrl: '/unacc/AM_HomeView_Read.jsp'
            }
          });
        } else if (p.match(/LOCALSHOP_\d+_\d+_\d+_\d+(_\d+)?/)) {
          const splittedParams:string[] = p.split('_');
          const localShopId:string = splittedParams[1];
          const webshopId:string = splittedParams[2];
          const countryId:string = splittedParams[3];
          const bpId:string = splittedParams[4];
          const requestId:string = splittedParams[5];

          const params:IObject = {
            keyfield: 'demence',
            keyvalue: 'WPL_EDIT',
            level: '2',
            previous: '1',
            withPics: '1',
            ejbEvent: 'Edit',
            Depcyid: localShopId,
            Desprid: webshopId,
            Decouid: countryId,
            Deccoid: bpId,
            Dereqid: requestId
          };

          messages.push({
            message: MESSAGE_CHANGE_MENU,
            data: {
              demence: 'WPL_EDIT',
              params: `&${urlUtil.createUrlQueryParams(params)}`,
              returnUrl: `/unacc/WP_ReqInfoView_Edit.jsp?row0_Desprid=${webshopId}&ejbEvent=Edit&wv_Template=WPN_SetUp.htm&wv_Detitle=Project%20setup`
            }
          });
        }
      }
    }
    return messages;
  }

  public getMenuParamValue(webshopId:number | string):string {
    return `WEBSHOP_${webshopId}`;
  }

  public onMenuChanged = (message:IGA1MenuMessage):void => {
    const menuParam:string = this.messageToMenuParam(message);
    if (this.currentMenuParam !== menuParam) {
      this.currentMenuParam = menuParam;
      console.debug('new menu param', this.currentMenuParam);
      const params:StateParams = this.stateService.params;
      if (this.currentMenuParam) {
        params.menu = this.currentMenuParam;
      } else {
        delete params.menu;
      }

      this.stateService.go(this.stateService.current.name, params, {
        location: 'replace',
        inherit: false
      });
    }
  };

  public onBeforeTransition = (transition:Transition):void => {
    const paramsCopy:IObject = _.extend({}, transition.params()); // Don't mutate the current parameters

    const menuParam:string = paramsCopy.menu;
    if (this.currentMenuParam !== menuParam) {
      console.debug('menu param changed: ' + menuParam);
      this.currentMenuParam = menuParam;
      this.sendChangeMenuMessage();
    }
  };

  private onMenuReady = ():void => {
    if (!this.menuReady) {
      this.menuReady = true;
      this.sendChangeMenuMessage();
    }
  };

  private loadMenu():void {
    if (this.menuReady) {
      this.sendChangeMenuMessage();
    }
  }

  private sendChangeMenuMessage():void {
    const messages:IGA1MenuMessage[] = this.menuParamToMessages(this.currentMenuParam);
    messages.forEach((message:IGA1MenuMessage) => this.ga1Service.postMessage(message));
  }

  public ngOnDestroy():void {
    this.ga1EventsSubscription?.unsubscribe();
    this.onBeforeStateHook();
  }
}